import type { CustomerProfile, CustomerTab, Row } from '@contract';
import { ArrowLeft01Icon, ArrowRight01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { formatMoney } from '@sao/shared/money';
import { useMutation, useQuery } from '@tanstack/react-query';
import { type ReactNode, useRef, useState } from 'react';
import { CASE_COLUMNS } from '@/components/cases';
import { DetailsCard, type DetailsReveal } from '@/components/customer-details';
import { DataTable } from '@/components/data-table';
import { ExportButton } from '@/components/export-button';
import { DocumentImages, KycReviewButton } from '@/components/kyc-detail';
import { DeliveryLog } from '@/components/messages';
import { ProposeChange, useCanPropose } from '@/components/propose-change';
import { ReasonDialog } from '@/components/reason-dialog';
import {
  type ColumnSpec,
  FieldList,
  RecordTable,
} from '@/components/record-table';
import { RefundOrCorrect } from '@/components/refund-or-correct';
import { useSheetActions } from '@/components/sheet/use-sheet';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LinkButton } from '@/components/ui/link-button';
import { Loading } from '@/components/ui/loading';
import { Select } from '@/components/ui/select';
import { callAdmin } from '@/lib/api';
import { adminQuery, queryClient } from '@/lib/query';
import { useCan } from '@/lib/staff-context';
import { toast } from '@/lib/toast';
import { cn } from '@/lib/utils';

export interface TransactionFilters {
  kind?: string;
  status?: string;
  from?: string;
  to?: string;
}

// ---------------------------------------------------------------------------
// Profile
// ---------------------------------------------------------------------------

const PROFILE_FIELDS: ColumnSpec[] = [
  { key: 'phone', label: 'Phone' },
  { key: 'name', label: 'Name' },
  { key: 'joined_at', label: 'Joined', kind: 'datetime' },
  { key: 'registration_step', label: 'Registration', kind: 'status' },
  { key: 'registration_completed_at', label: 'Registered', kind: 'datetime' },
  { key: 'verified', label: 'Verified', kind: 'bool' },
  { key: 'verified_at', label: 'Verified at', kind: 'datetime' },
  { key: 'kyc_attempt', label: 'Verification attempts', kind: 'number' },
  { key: 'limit_group', label: 'Limit group' },
  { key: 'archived_at', label: 'Archived', kind: 'datetime' },
  { key: 'discoverable_by_phone', label: 'Findable by phone', kind: 'bool' },
];

const HOLD_REASON: Record<string, string> = {
  pin_reset: 'PIN reset',
  new_device: 'New device',
  emergency: 'Emergency',
};

export function ProfileTab({
  profile,
  reveal,
}: Readonly<{ profile: CustomerProfile; reveal: DetailsReveal }>) {
  const hold = profile.money_out_hold;
  const pin = profile.pin;
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <DetailsCard reveal={reveal} />
      <Card>
        <CardHeader>
          <CardTitle>Account</CardTitle>
        </CardHeader>
        <CardContent>
          <FieldList
            fields={PROFILE_FIELDS.filter(
              (field) =>
                !(
                  reveal.details &&
                  (field.key === 'phone' || field.key === 'name')
                ),
            )}
            record={profile as unknown as Row}
          />
        </CardContent>
      </Card>
      <div className="flex flex-col gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Money-out hold</CardTitle>
            <CardDescription>
              {hold?.active
                ? `${HOLD_REASON[hold.reason ?? ''] ?? 'Hold'}, until it ends.`
                : 'No hold in place.'}
            </CardDescription>
          </CardHeader>
          {hold ? (
            <CardContent>
              <FieldList
                fields={[
                  { key: 'reason', label: 'Reason', kind: 'status' },
                  { key: 'started_at', label: 'Started', kind: 'datetime' },
                  { key: 'until', label: 'Ends', kind: 'datetime' },
                ]}
                record={hold as unknown as Row}
              />
            </CardContent>
          ) : null}
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>PIN</CardTitle>
            <CardDescription>
              Staff never see or set a PIN. The customer resets it in the app.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {pin ? (
              <FieldList
                fields={[
                  { key: 'set_at', label: 'Set', kind: 'datetime' },
                  { key: 'locked', label: 'Locked', kind: 'bool' },
                  { key: 'locked_until', label: 'Unlocks', kind: 'datetime' },
                  {
                    key: 'failed_attempts',
                    label: 'Wrong tries',
                    kind: 'number',
                  },
                  { key: 'lock_count', label: 'Times locked', kind: 'number' },
                  {
                    key: 'reset_required',
                    label: 'Reset needed',
                    kind: 'bool',
                  },
                ]}
                record={pin as unknown as Row}
              />
            ) : (
              <p className="text-sm text-muted-foreground">No PIN set yet.</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Sign-in limits</CardTitle>
          </CardHeader>
          <CardContent>
            <RecordTable
              columns={[
                { key: 'key', label: 'Limit' },
                { key: 'count', label: 'Tries', kind: 'number' },
                { key: 'window_start', label: 'Since', kind: 'datetime' },
              ]}
              rows={profile.sign_in_limits}
              empty="None in force."
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/** Freeze now (one person), or propose a restriction or a correction. */
export function WalletActions({ wallet }: Readonly<{ wallet: Row }>) {
  const canFreeze = useCan('wallets.freeze_now');
  const canCredit = useCanPropose('deposit_credit');
  const [freezing, setFreezing] = useState(false);
  const walletId = String(wallet.id);
  const currency = String(wallet.currency);
  const freeze = useMutation({
    mutationFn: (reason: string) =>
      callAdmin('wallets.freeze_now', { wallet_id: walletId, reason }),
    onSuccess: async () => {
      setFreezing(false);
      await queryClient.invalidateQueries({ queryKey: ['admin'] });
      toast.success('Frozen. A checker confirms it within 24 hours.');
    },
  });
  return (
    <div className="flex flex-wrap gap-2 pt-2">
      {canFreeze ? (
        <Button
          size="sm"
          variant="destructive"
          onClick={() => setFreezing(true)}
        >
          Freeze now
        </Button>
      ) : null}
      <ProposeChange
        kind="restrict"
        payload={{ wallet_id: walletId }}
        label="Restrict"
        title="Restrict this wallet"
        description="Stop money out, money in, or both, until it is lifted."
        fields={[
          {
            name: 'kind',
            label: 'Stop',
            kind: 'select',
            options: [
              { value: 'full', label: 'Money in and out' },
              { value: 'no_debit', label: 'Money out' },
              { value: 'no_credit', label: 'Money in' },
            ],
          },
          {
            name: 'reason',
            label: 'Why',
            kind: 'select',
            options: [
              { value: 'operator', label: 'Operator restriction' },
              { value: 'risk_review', label: 'Risk review' },
            ],
          },
        ]}
      />
      {canCredit ? (
        <LinkButton
          to="/customers/credit/$walletId"
          params={{ walletId }}
          search={{
            number: String(wallet.wallet_number),
            name: String(wallet.name),
            currency,
          }}
        >
          Credit customer
        </LinkButton>
      ) : null}
      <RefundOrCorrect
        wallets={[
          {
            id: walletId,
            number: String(wallet.wallet_number),
            label: 'Wallet',
          },
        ]}
        currency={currency}
      />
      <ReasonDialog
        open={freezing}
        onOpenChange={setFreezing}
        title="Freeze this wallet now"
        description="Nothing moves in or out from now. One person may freeze; a checker confirms it, and unfreezing is a change request."
        confirmLabel="Freeze now"
        pending={freeze.isPending}
        error={freeze.error?.message ?? null}
        onConfirm={(reason) => freeze.mutate(reason)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// The other tabs
// ---------------------------------------------------------------------------

/** One tab's data from customers.tab. */
export function useTab<T>(
  customerId: string,
  tab: CustomerTab,
  extra: Row = {},
) {
  return useQuery({
    ...adminQuery('customers.tab', { customer_id: customerId, tab, ...extra }),
    select: (data) => data as T,
  });
}

export function TabState({
  isPending,
  error,
}: Readonly<{ isPending: boolean; error: Error | null }>) {
  if (error) return <Alert variant="destructive">{error.message}</Alert>;
  if (isPending) return <Loading />;
  return null;
}

export function VerificationTab({
  customerId,
}: Readonly<{ customerId: string }>) {
  const tab = useTab<{
    identity: Row | null;
    sessions: Row[];
    attempts: Row[];
  }>(customerId, 'verification');
  return (
    <div className="flex flex-col gap-6">
      <TabState isPending={tab.isPending} error={tab.error} />
      <Card>
        <CardHeader>
          <CardTitle>Identity</CardTitle>
          <CardDescription>
            The ID number and document images open only on a Compliance reveal.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {tab.data?.identity ? (
            <FieldList
              fields={[
                { key: 'name', label: 'Name' },
                { key: 'email', label: 'Email' },
                { key: 'document_type', label: 'Document', kind: 'status' },
                { key: 'issuing_country', label: 'Issued in' },
                { key: 'id_number', label: 'ID number' },
                { key: 'id_expiry', label: 'Expires', kind: 'date' },
                { key: 'region', label: 'Region' },
                { key: 'submitted_at', label: 'Submitted', kind: 'datetime' },
              ]}
              record={tab.data.identity}
            />
          ) : (
            <p className="text-sm text-muted-foreground">No identity yet.</p>
          )}
          {tab.data?.identity ? (
            <IdentityReveal customerId={customerId} />
          ) : null}
        </CardContent>
      </Card>
      <section className="flex flex-col gap-2">
        <h2 className="font-semibold">Verification sessions</h2>
        <RecordTable
          columns={[
            { key: 'created_at', label: 'Started', kind: 'datetime' },
            { key: 'purpose', label: 'Purpose' },
            { key: 'provider', label: 'Provider' },
            { key: 'status', label: 'Status', kind: 'status' },
            { key: 'document_type', label: 'Document' },
            { key: 'name', label: 'Name on ID' },
            { key: 'decision_received_at', label: 'Decided', kind: 'datetime' },
            { key: 'provider_code', label: 'Margins code', kind: 'mono' },
            { key: 'provider_reference', label: 'Reference', kind: 'mono' },
          ]}
          rows={tab.data?.sessions}
          empty="No sessions."
          renderActions={(session) => (
            <KycReviewButton
              sessionId={String(session.session_id)}
              title="Verification session"
              label="Details"
              provider={String(session.provider)}
            />
          )}
        />
      </section>
      <section className="flex flex-col gap-2">
        <h2 className="font-semibold">Verification attempts</h2>
        <RecordTable
          columns={[
            { key: 'created_at', label: 'When', kind: 'datetime' },
            { key: 'provider', label: 'Provider' },
            { key: 'mode', label: 'Mode' },
            { key: 'status', label: 'Status', kind: 'status' },
            { key: 'error_code', label: 'Error', kind: 'mono' },
          ]}
          rows={tab.data?.attempts}
          empty="No attempts."
        />
      </section>
    </div>
  );
}

function IdentityReveal({ customerId }: Readonly<{ customerId: string }>) {
  const canReveal = useCan('customers.reveal_identity');
  const [open, setOpen] = useState(false);
  const reveal = useMutation({
    mutationFn: (reason: string) =>
      callAdmin('customers.reveal_identity', {
        customer_id: customerId,
        reason,
      }),
    onSuccess: () => setOpen(false),
  });
  if (!canReveal) return null;

  return (
    <div className="flex flex-col gap-4">
      {reveal.data ? (
        <Alert variant="warning">
          <div className="flex flex-col gap-3">
            <span>
              ID number{' '}
              <span className="font-mono font-semibold">
                {reveal.data.id_number}
              </span>{' '}
              · {reveal.data.full_name} · born {reveal.data.date_of_birth}. The
              images below stop working after a minute.
            </span>
            <DocumentImages
              documents={reveal.data.documents}
              note="These links stop working after a minute."
            />
          </div>
        </Alert>
      ) : (
        <Button
          variant="outline"
          className="w-fit"
          onClick={() => setOpen(true)}
        >
          Reveal the ID number and images
        </Button>
      )}
      <ReasonDialog
        open={open}
        onOpenChange={setOpen}
        title="Reveal identity documents"
        description="For this view only. The reveal and its reason are logged against you."
        confirmLabel="Reveal"
        pending={reveal.isPending}
        error={reveal.error?.message ?? null}
        onConfirm={(reason) => reveal.mutate(reason)}
      />
    </div>
  );
}

type WalletShow = 'open' | 'closed' | 'all';

const isClosed = (wallet: Row) => wallet.status === 'closed';

/**
 * A customer's wallets, the ones in use first. Closed wallets are kept out of
 * the way: the list opens on the wallets in use, and "Show" switches to the
 * closed ones or to all of them.
 */
export function WalletsTab({ customerId }: Readonly<{ customerId: string }>) {
  const tab = useTab<Row[]>(customerId, 'wallets');
  const [show, setShow] = useState<WalletShow>('open');
  const wallets = tab.data ?? [];
  const closed = wallets.filter(isClosed);
  const open = wallets.filter((wallet) => !isClosed(wallet));
  const lists: Record<WalletShow, Row[]> = {
    open,
    closed,
    all: [...open, ...closed],
  };
  const shown = lists[show];
  return (
    <div className="flex flex-col gap-4">
      <TabState isPending={tab.isPending} error={tab.error} />
      {wallets.length > 0 ? (
        <Select
          label="Show"
          className="w-full sm:w-56"
          value={show}
          onChange={(event) => setShow(event.target.value as WalletShow)}
        >
          <option value="open">In use ({open.length})</option>
          <option value="closed">Closed ({closed.length})</option>
          <option value="all">All wallets ({wallets.length})</option>
        </Select>
      ) : null}
      {tab.data && shown.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {emptyWallets(show, wallets)}
        </p>
      ) : null}
      {shown.length > 0 ? <WalletRow wallets={shown} /> : null}
    </div>
  );
}

/**
 * The wallets side by side in one row that is swiped (or scrolled) sideways,
 * each stopping at its edge. The arrows do the same for a mouse.
 */
function WalletRow({ wallets }: Readonly<{ wallets: Row[] }>) {
  const row = useRef<HTMLUListElement>(null);
  const move = (direction: 1 | -1) => {
    const list = row.current;
    const card = list?.firstElementChild;
    if (!list || !card) return;
    list.scrollBy({
      left: direction * (card.getBoundingClientRect().width + 16),
      behavior: 'smooth',
    });
  };
  return (
    <div className="flex min-w-0 flex-col gap-2">
      {wallets.length > 1 ? (
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            {wallets.length} wallets. Swipe sideways to see each.
          </p>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              aria-label="Previous wallet"
              onClick={() => move(-1)}
            >
              <HugeiconsIcon icon={ArrowLeft01Icon} size={16} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              aria-label="Next wallet"
              onClick={() => move(1)}
            >
              <HugeiconsIcon icon={ArrowRight01Icon} size={16} />
            </Button>
          </div>
        </div>
      ) : null}
      <ul
        ref={row}
        className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2"
      >
        {wallets.map((wallet) => (
          <li
            key={String(wallet.id)}
            className={cn(
              'min-w-0 shrink-0 snap-start',
              wallets.length > 1 ? 'w-[88%] md:w-[34rem]' : 'w-full',
            )}
          >
            <WalletCard wallet={wallet} />
          </li>
        ))}
      </ul>
    </div>
  );
}

/** A wallet in use is green, a closed one is grey, anything else stands out. */
/** What the wallet can do today: a restriction shows even while the status stays active. */
function walletState(wallet: Row): {
  label: string;
  tone: 'success' | 'muted' | 'warning';
} {
  const status = String(wallet.status);
  if (status === 'closed') return { label: 'closed', tone: 'muted' };
  if (wallet.debits_blocked && wallet.credits_blocked) {
    return { label: 'frozen', tone: 'warning' };
  }
  if (wallet.debits_blocked || wallet.credits_blocked) {
    return { label: 'restricted', tone: 'warning' };
  }
  return { label: status, tone: status === 'active' ? 'success' : 'warning' };
}

function emptyWallets(show: WalletShow, wallets: Row[]): string {
  if (wallets.length === 0) return 'No wallets.';
  return show === 'open'
    ? `No wallet in use. ${wallets.length} closed: choose Closed to see them.`
    : 'No closed wallets.';
}

function WalletCard({ wallet }: Readonly<{ wallet: Row }>) {
  const currency = String(wallet.currency);
  const restrictions = (wallet.restrictions as Row[]) ?? [];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          <span>
            {String(wallet.name)} · {currency}
          </span>
          <Badge variant={walletState(wallet).tone}>
            {walletState(wallet).label}
          </Badge>
        </CardTitle>
        <CardDescription>
          {String(wallet.kind)} wallet {String(wallet.wallet_number)},{' '}
          {wallet.owner ? 'owner' : `${String(wallet.role)} (member)`}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-6 text-sm">
          <span>
            Balance{' '}
            <strong className="tabular-nums">
              {formatMoney(Number(wallet.posted_minor), currency)}
            </strong>
          </span>
          <span>
            Held{' '}
            <strong className="tabular-nums">
              {formatMoney(Number(wallet.held_minor), currency)}
            </strong>
          </span>
          <span>
            Available{' '}
            <strong className="tabular-nums">
              {formatMoney(
                Number(wallet.posted_minor) - Number(wallet.held_minor),
                currency,
              )}
            </strong>
          </span>
          {wallet.debits_blocked ? <span>Debits blocked</span> : null}
          {wallet.credits_blocked ? <span>Credits blocked</span> : null}
        </div>
        {wallet.rules ? (
          <FieldList
            fields={[
              {
                key: 'approvals_required',
                label: 'Approvals needed',
                kind: 'number',
              },
              {
                key: 'approval_threshold_minor',
                label: 'Approval above',
                kind: 'money',
              },
              { key: 'funding_policy', label: 'Who may fund' },
              { key: 'withdraw_policy', label: 'Who may withdraw' },
              {
                key: 'new_members_can_send',
                label: 'New members send',
                kind: 'bool',
              },
            ]}
            record={{ ...(wallet.rules as Row), currency }}
          />
        ) : null}
        {wallet.permissions ? (
          <FieldList
            fields={[
              { key: 'can_send', label: 'Can send', kind: 'bool' },
              { key: 'can_fund', label: 'Can fund', kind: 'bool' },
              { key: 'can_approve', label: 'Can approve', kind: 'bool' },
              { key: 'can_withdraw', label: 'Can withdraw', kind: 'bool' },
              { key: 'spend_limit_minor', label: 'Spend limit', kind: 'money' },
              {
                key: 'restricted_until',
                label: 'Restricted until',
                kind: 'datetime',
              },
            ]}
            record={{ ...(wallet.permissions as Row), currency }}
          />
        ) : null}
        {restrictions.length > 0 ? (
          <RecordTable
            columns={[
              { key: 'kind', label: 'Restriction', kind: 'status' },
              { key: 'reason', label: 'Reason' },
              { key: 'set_at', label: 'Set', kind: 'datetime' },
              { key: 'lifted_at', label: 'Lifted', kind: 'datetime' },
            ]}
            rows={restrictions}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}

const TRANSACTION_COLUMNS: ColumnSpec[] = [
  { key: 'id', label: 'Transaction', kind: 'transaction' },
  { key: 'created_at', label: 'When', kind: 'datetime' },
  { key: 'type', label: 'Kind' },
  { key: 'processor', label: 'Processed by' },
  { key: 'status', label: 'Status', kind: 'status' },
  { key: 'amount_minor', label: 'Amount', kind: 'money' },
  { key: 'fee_minor', label: 'Fee', kind: 'money' },
  { key: 'source_wallet_number', label: 'From', kind: 'mono' },
  { key: 'destination_wallet_number', label: 'To', kind: 'mono' },
  { key: 'provider_reference', label: 'Reference', kind: 'mono' },
  { key: 'failure_code', label: 'Failure', kind: 'mono' },
];

export function TransactionsTab({
  customerId,
  filters,
  onFilters,
}: Readonly<{
  customerId: string;
  filters: TransactionFilters;
  onFilters: (filters: TransactionFilters) => void;
}>) {
  const [draft, setDraft] = useState(filters);
  const args = {
    kind: filters.kind,
    status: filters.status,
    from: filters.from ? `${filters.from}T00:00:00Z` : undefined,
    to: filters.to ? `${filters.to}T23:59:59.999Z` : undefined,
  };
  const tab = useTab<{ rows: Row[] }>(customerId, 'transactions', args);
  return (
    <div className="flex flex-col gap-4">
      <form
        className="grid grid-cols-2 items-end gap-3 sm:flex sm:flex-wrap"
        onSubmit={(event) => {
          event.preventDefault();
          onFilters(draft);
        }}
      >
        <div className="flex flex-col gap-1">
          <Label htmlFor="tx-kind">Kind</Label>
          <Input
            id="tx-kind"
            className="w-full sm:w-36"
            placeholder="transfer"
            value={draft.kind ?? ''}
            onChange={(event) =>
              setDraft({ ...draft, kind: event.target.value || undefined })
            }
          />
        </div>
        <div className="flex flex-col gap-1">
          <Select
            label="Status"
            className="w-full sm:w-40"
            value={draft.status ?? ''}
            onChange={(event) =>
              setDraft({ ...draft, status: event.target.value || undefined })
            }
          >
            <option value="">Any</option>
            {[
              'awaiting_approval',
              'pending',
              'posted',
              'failed',
              'reversed',
            ].map((status) => (
              <option key={status} value={status}>
                {status.replaceAll('_', ' ')}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <DatePicker
            label="From"
            value={draft.from ?? ''}
            onChange={(event) =>
              setDraft({ ...draft, from: event.target.value || undefined })
            }
          />
        </div>
        <div className="flex flex-col gap-1">
          <DatePicker
            label="To"
            value={draft.to ?? ''}
            onChange={(event) =>
              setDraft({ ...draft, to: event.target.value || undefined })
            }
          />
        </div>
        <Button type="submit">Filter</Button>
        <ExportButton
          source="customers.tab"
          args={{ customer_id: customerId, tab: 'transactions', ...args }}
        />
      </form>
      <TabState isPending={tab.isPending} error={tab.error} />
      <RecordTable
        columns={TRANSACTION_COLUMNS}
        rows={tab.data?.rows}
        empty="No transactions match."
      />
    </div>
  );
}

export function ActivityTab({ customerId }: Readonly<{ customerId: string }>) {
  const tab = useTab<Row[]>(customerId, 'activity');
  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <ExportButton
          source="customers.tab"
          args={{ customer_id: customerId, tab: 'activity' }}
        />
      </div>
      <TabState isPending={tab.isPending} error={tab.error} />
      <RecordTable
        columns={[
          { key: 'at', label: 'When', kind: 'datetime' },
          { key: 'source', label: 'Area', kind: 'status' },
          { key: 'event', label: 'Event', kind: 'words' },
          { key: 'ip', label: 'IP', kind: 'mono' },
          { key: 'detail', label: 'Detail', kind: 'json' },
        ]}
        rows={tab.data}
        empty="No activity."
      />
    </div>
  );
}

export function DevicesTab({ customerId }: Readonly<{ customerId: string }>) {
  const tab = useTab<{
    devices: Row[];
    pay_credentials: Row[];
    device_credentials: Row[];
    push_tokens: Row[];
  }>(customerId, 'devices');
  return (
    <div className="flex flex-col gap-6">
      <TabState isPending={tab.isPending} error={tab.error} />
      <Section title="Bound phones">
        <RecordTable
          columns={[
            { key: 'model', label: 'Model' },
            { key: 'platform', label: 'Platform' },
            { key: 'os_version', label: 'OS' },
            { key: 'app_version', label: 'App' },
            { key: 'created_at', label: 'Bound', kind: 'datetime' },
            { key: 'last_seen_at', label: 'Last seen', kind: 'datetime' },
            { key: 'revoked_at', label: 'Revoked', kind: 'datetime' },
          ]}
          rows={tab.data?.devices}
          empty="No phones."
        />
      </Section>
      <Section title="Biometric pay credentials">
        <RecordTable
          columns={[
            { key: 'device_id', label: 'Device', kind: 'mono' },
            { key: 'created_at', label: 'Added', kind: 'datetime' },
            { key: 'last_used_at', label: 'Last used', kind: 'datetime' },
            { key: 'revoked_at', label: 'Revoked', kind: 'datetime' },
          ]}
          rows={tab.data?.pay_credentials}
          empty="None."
        />
      </Section>
      <Section title="Push tokens">
        <RecordTable
          columns={[
            { key: 'device_id', label: 'Device', kind: 'mono' },
            { key: 'platform', label: 'Platform' },
            { key: 'updated_at', label: 'Updated', kind: 'datetime' },
            { key: 'revoked_at', label: 'Revoked', kind: 'datetime' },
            { key: 'last_error', label: 'Last error', kind: 'mono' },
          ]}
          rows={tab.data?.push_tokens}
          empty="None."
        />
      </Section>
    </div>
  );
}

export function SavedTab({ customerId }: Readonly<{ customerId: string }>) {
  const tab = useTab<Record<string, Row[]>>(customerId, 'saved');
  return (
    <div className="flex flex-col gap-6">
      <TabState isPending={tab.isPending} error={tab.error} />
      <Section title="Beneficiaries">
        <RecordTable
          columns={[
            { key: 'kind', label: 'Kind' },
            { key: 'label', label: 'Label' },
            { key: 'masked', label: 'Account' },
            { key: 'last_used_at', label: 'Last used', kind: 'datetime' },
          ]}
          rows={tab.data?.beneficiaries}
          empty="None."
        />
      </Section>
      <Section title="Funding numbers">
        <RecordTable
          columns={[
            { key: 'name', label: 'Name' },
            { key: 'phone', label: 'Number' },
            { key: 'network', label: 'Network' },
            { key: 'momo_name', label: 'Name on the account' },
            { key: 'created_at', label: 'Added', kind: 'datetime' },
          ]}
          rows={tab.data?.funding_numbers}
          empty="None."
        />
      </Section>
      <Section title="Saved bills">
        <RecordTable
          columns={[
            { key: 'biller', label: 'Biller' },
            { key: 'label', label: 'Label' },
            { key: 'account', label: 'Account' },
            { key: 'last_amount_minor', label: 'Last amount', kind: 'money' },
            { key: 'last_paid_at', label: 'Last paid', kind: 'datetime' },
          ]}
          rows={tab.data?.saved_bills}
          empty="None."
        />
      </Section>
      <Section title="Schedules">
        <RecordTable
          columns={[
            { key: 'frequency', label: 'Every' },
            { key: 'amount_minor', label: 'Amount', kind: 'money' },
            { key: 'starts_on', label: 'Starts', kind: 'date' },
            { key: 'rounds', label: 'Rounds', kind: 'number' },
            { key: 'status', label: 'Status', kind: 'status' },
            { key: 'round_log', label: 'Rounds so far', kind: 'json' },
          ]}
          rows={tab.data?.schedules}
          empty="None."
        />
      </Section>
      <Section title="Contributions">
        <RecordTable
          columns={[
            { key: 'frequency', label: 'Every' },
            { key: 'amount_minor', label: 'Amount', kind: 'money' },
            { key: 'status', label: 'Status', kind: 'status' },
            { key: 'declines', label: 'Declined', kind: 'number' },
            { key: 'round_log', label: 'Rounds', kind: 'json' },
          ]}
          rows={tab.data?.contributions}
          empty="None."
        />
      </Section>
      <Section title="Withdrawal destinations">
        <RecordTable
          columns={[
            { key: 'rail', label: 'Rail' },
            { key: 'network', label: 'Network' },
            { key: 'phone', label: 'Number' },
            { key: 'account', label: 'Account' },
            { key: 'recipient_name', label: 'Name' },
            { key: 'amount_minor', label: 'Amount', kind: 'money' },
            { key: 'created_at', label: 'When', kind: 'datetime' },
          ]}
          rows={tab.data?.withdrawal_destinations}
          empty="None."
        />
      </Section>
    </div>
  );
}

/** Moves a customer to another limit group, picked from the groups. */
export function LimitGroupChange({
  customerId,
  current,
}: Readonly<{ customerId: string; current: string | null }>) {
  const canList = useCan('limits.list');
  const groups = useQuery({
    ...adminQuery('limits.list', {}),
    enabled: canList,
  });
  const options = groups.data?.groups
    .filter((group) => group.code !== current)
    .map((group) => ({
      value: group.code,
      label: `${group.name} (${group.code})`,
    }));
  return (
    <ProposeChange
      kind="limit_group"
      payload={{ customer_id: customerId }}
      label="Change limit group"
      title="Move this customer to another limit group"
      description="Their limits change to the group's limits."
      fields={[
        options && options.length > 0
          ? {
              name: 'limit_group',
              label: 'Limit group',
              kind: 'select',
              options,
            }
          : {
              name: 'limit_group',
              label: 'Limit group code',
              kind: 'text',
              hint: 'The code of an existing limit group, such as default.',
            },
      ]}
    />
  );
}

export function MessagesTab({ customerId }: Readonly<{ customerId: string }>) {
  const tab = useTab<{ notices: Row[]; push: Row[] }>(customerId, 'messages');
  return (
    <div className="flex flex-col gap-6">
      <TabState isPending={tab.isPending} error={tab.error} />
      <Section title="Sent to the customer">
        <DeliveryLog customerId={customerId} />
      </Section>
      <Section title="Notices">
        <RecordTable
          columns={[
            { key: 'created_at', label: 'When', kind: 'datetime' },
            { key: 'title', label: 'Title' },
            { key: 'body', label: 'Message' },
            { key: 'sms', label: 'SMS', kind: 'bool' },
            { key: 'delivered_at', label: 'Delivered', kind: 'datetime' },
            { key: 'last_error', label: 'Error', kind: 'mono' },
            { key: 'read_at', label: 'Read', kind: 'datetime' },
          ]}
          rows={tab.data?.notices}
          empty="No notices."
        />
      </Section>
      <Section title="Push">
        <RecordTable
          columns={[
            { key: 'sent_at', label: 'Sent', kind: 'datetime' },
            { key: 'event', label: 'Event', kind: 'mono' },
            { key: 'receipt_status', label: 'Receipt', kind: 'status' },
            { key: 'receipt_error', label: 'Error', kind: 'mono' },
          ]}
          rows={tab.data?.push}
          empty="No push messages."
        />
      </Section>
    </div>
  );
}

export function IssuesTab({ customerId }: Readonly<{ customerId: string }>) {
  const tab = useTab<Row[]>(customerId, 'issues');
  const cases = useQuery(adminQuery('cases.list', { customer_id: customerId }));
  const sheetActions = useSheetActions();
  return (
    <div className="flex flex-col gap-6">
      <TabState isPending={tab.isPending} error={tab.error} />
      <Section title="Reports">
        <RecordTable
          columns={ISSUE_COLUMNS.filter((column) => column.key !== 'customer')}
          rows={tab.data}
          empty="No reports."
        />
      </Section>
      <Section title="Cases">
        {cases.isError ? (
          <Alert variant="destructive">{cases.error.message}</Alert>
        ) : null}
        <DataTable
          columns={CASE_COLUMNS}
          data={cases.data?.rows}
          empty="No cases."
          rowLabel={(row) => `Open case ${row.id.slice(0, 8)}`}
          onRowClick={(row, rowEl) =>
            sheetActions.open(
              { type: 'case', id: row.id },
              { fromRowEl: rowEl.querySelector('button') ?? rowEl },
            )
          }
        />
      </Section>
    </div>
  );
}

const ISSUE_COLUMNS: ColumnSpec[] = [
  { key: 'id', label: 'Report', kind: 'issue' },
  { key: 'customer', label: 'Customer', kind: 'customer' },
  { key: 'status', label: 'Status', kind: 'status' },
  { key: 'product', label: 'Product' },
  { key: 'amount_minor', label: 'Amount', kind: 'money' },
  { key: 'owner', label: 'Owner', kind: 'staff' },
  { key: 'created_at', label: 'Filed', kind: 'datetime' },
  { key: 'due_at', label: 'Due', kind: 'datetime' },
];

export function StatementTab({ customerId }: Readonly<{ customerId: string }>) {
  const wallets = useTab<Row[]>(customerId, 'wallets');
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86_400_000)
    .toISOString()
    .slice(0, 10);
  const [walletId, setWalletId] = useState('');
  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(today);
  const statement = useMutation({
    mutationFn: () =>
      callAdmin('customers.statement', {
        customer_id: customerId,
        wallet_id: walletId,
        from: `${from}T00:00:00Z`,
        to: `${to}T23:59:59.999Z`,
      }),
  });
  const data = statement.data as
    | {
        currency: string;
        openingMinor: number;
        closingMinor: number;
        creditsMinor: number;
        debitsMinor: number;
        lines: Row[];
      }
    | undefined;

  return (
    <div className="flex flex-col gap-4">
      <form
        className="grid grid-cols-2 items-end gap-3 sm:flex sm:flex-wrap"
        onSubmit={(event) => {
          event.preventDefault();
          statement.mutate();
        }}
      >
        <div className="flex flex-col gap-1">
          <Select
            label="Wallet"
            className="w-full sm:w-56"
            value={walletId}
            onChange={(event) => setWalletId(event.target.value)}
          >
            <option value="">Choose…</option>
            {wallets.data?.map((wallet) => (
              <option key={String(wallet.id)} value={String(wallet.id)}>
                {String(wallet.name)} ({String(wallet.currency)})
              </option>
            ))}
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <DatePicker
            label="From"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <DatePicker
            label="To"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
        <Button type="submit" disabled={!walletId || statement.isPending}>
          Show statement
        </Button>
      </form>
      {statement.isError ? (
        <Alert variant="destructive">{statement.error.message}</Alert>
      ) : null}
      {data ? (
        <>
          <div className="flex flex-wrap gap-6 text-sm">
            <span>Opening {formatMoney(data.openingMinor, data.currency)}</span>
            <span>In {formatMoney(data.creditsMinor, data.currency)}</span>
            <span>Out {formatMoney(data.debitsMinor, data.currency)}</span>
            <span>Closing {formatMoney(data.closingMinor, data.currency)}</span>
          </div>
          <RecordTable
            columns={[
              { key: 'at', label: 'When', kind: 'datetime' },
              {
                key: 'transactionId',
                label: 'Transaction',
                kind: 'transaction',
              },
              { key: 'kind', label: 'Kind' },
              { key: 'netMinor', label: 'Amount', kind: 'money' },
              { key: 'balanceAfterMinor', label: 'Balance', kind: 'money' },
            ]}
            rows={data.lines.map((line) => ({
              ...line,
              currency: data.currency,
            }))}
            empty="No movements in this period."
          />
        </>
      ) : null}
    </div>
  );
}

export function Section({
  title,
  actions,
  children,
}: Readonly<{ title: string; actions?: ReactNode; children: ReactNode }>) {
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-semibold">{title}</h2>
        {actions}
      </div>
      {children}
    </section>
  );
}
